import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { IAlertsService } from '../../../../../../infrastructure/alerts';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { alertServerError } from '../../../../../global/modal/alerts-host/alert-templates';
import { CustomPageConfigurationType } from '../../../../../../api/custom-pages.dao';
import { IAllPagesConfigurationDTO, IWebsiteDAO } from '../../../../../../api/website.dao';
import { defOrderConfig, orderPages } from '../helpers';
import { DefaultPageEntityConfig } from '../../../../../../modules/passport-pages/default-pages/default-page-entity.config';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../../../../../tracking-service/tracking-values';

export default class ConfPassNavFormVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }
    @mobx.observable
    public isSaving: boolean;
    @mobx.observable
    public isLoading: boolean;
    @mobx.observable
    public pages: CustomPageConfigurationType[];

    private readonly intl: IntlShape;

    constructor(
        private readonly accountId: number,
        private readonly eventId: number,
        private readonly setIsResetCustomPages: () => void,
        private readonly onSuccess: ( pages: CustomPageConfigurationType[]) => void,
        @IoC.Inject private readonly websiteDAO: IWebsiteDAO = IoC.Container.get(IWebsiteDAO),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider)
    ) {
        this.isSaving = false;
        this.isLoading = false;
    }

    public get pagesList(): CustomPageConfigurationType[] {
        return this.pages;
    }

    private get eventParams(): { accountId: number; eventId: number } {
        return { accountId: this.accountId, eventId: this.eventId };
    }

    @mobx.action.bound
    private async initPages(): Promise<void> {
        try {
            const { pages } = await this.websiteDAO.getAllPagesConfiguration(this.eventParams);
            this.setPages(pages);
        } catch (e) {
            ConfPassNavFormVM.handleApiException(e);
        }
    }

    private isPageDefault(page: CustomPageConfigurationType): boolean {
        return (DefaultPageEntityConfig.getPageTypes() as string[]).includes(page.type);
    }

    @mobx.action.bound
    public setPages(pages: CustomPageConfigurationType[]): void {
        const { enablePages, pagesList } = orderPages(pages);

        const isNotExistActiveLandingPage = !enablePages.some(page => Boolean(page.landingPage) && page.enabled);
        this.pages = pagesList;
        if (isNotExistActiveLandingPage) {
            let customPage = null;
            let defPage = null;
            const keys = Object.keys(defOrderConfig);
            for (const key in defOrderConfig) {
                if (!defPage) {
                    defPage = enablePages.find(page => {
                        const isNotDefPage = keys.includes(page.type.toString());
                        if (!isNotDefPage && page.enabled) {
                            customPage = page;
                        }
                        return page.enabled && key === page.type;
                    });
                }
            }

            const pageId = defPage?.type || customPage?.customPageId;
            if (pageId) {
                this.setLandingPage(pageId.toString());
            }
        }
    }

    @mobx.action.bound
    public updatePagesOrder = (pages): void => {
        let order = 1;
        const list = pages.map(page => ({ ...page, order: order++ }));
        this.setPages(list);
    };

    @mobx.action.bound
    public async initialize(): Promise<void[]> {
        try {
            return Promise.all([this.initPages].map(async fn => await fn()));
        } catch (e) {
            ConfPassNavFormVM.handleApiException(e);
        }
    }

    @mobx.action.bound
    public setLandingPage(id: string): void {
        this.setPages(
            this.pages.map(page => {
                return { ...page, landingPage: id === (page.customPageId || page.type).toString() ? true : false };
            })
        );
    }

    @mobx.action.bound
    public onChangePageEnabledState = (id: string, newValue: boolean): void => {
        this.setPages(
            this.pages.map(page => {
                return {
                    ...page,
                    enabled: id === (page.customPageId || page.type).toString() ? newValue : page.enabled,
                };
            })
        );
    };

    @mobx.action
    public applyConfPassportNavForm = async (): Promise<boolean> => {
        try {
            this.isSaving = true;
            const updateDTO: IAllPagesConfigurationDTO = {
                pages: this.pagesList.map(page => {
                    const { customPageId, ...rest } = page;
                    return {
                        ...rest,
                        ...(!this.isPageDefault(page) && { customPageId: customPageId }),
                    };
                }),
            };
            const { pages } = await this.websiteDAO.updateAllPagesConfiguration(this.eventParams, updateDTO);
            this.onSuccess(pages);
            this.trackingService.get().track(TRACKING_EVENTS.NAVIGATION_CONFIGURATION, {});
            return true;
        } catch (e) {
            ConfPassNavFormVM.handleApiException(e);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.setIsResetCustomPages();
                this.isSaving = false;
            });
        }
    };
}
